/**
 * Transport for the affiliate (influencer) endpoints. Thin wrappers over the
 * single shared `apiClient`; no mapping and no business rules.
 *
 * Calls the affiliate portal makes accept an `auth` argument carrying the
 * affiliate's own bearer. Omitting it falls back to the member session, which
 * is what the control panel wants for the operator-only routes.
 */
import type { AxiosResponse } from 'axios';
import apiClient from '../apiClient';
import AFFILIATE_ENDPOINTS from './affiliateEndpoints';
import type {
  AffiliateAdminReviewRequest,
  AffiliateGetAllRequest,
  AffiliateGetAllResponse,
  AffiliateGetByIdRequest,
  AffiliateGetByIdResponse,
  AffiliateMutationResponse,
  AffiliateReferralCodeDeleteRequest,
  AffiliateReferralCodeGetListRequest,
  AffiliateReferralCodeGetListResponse,
  AffiliateReferralCodeUpdateRequest,
  AffiliateRegisterRequest,
  AffiliateRegisterResponse,
  AffiliateUpdateProfileRequest,
  AffiliateUpdateProfileResponse,
  AffiliateValidateLoginRequest,
  AffiliateValidateLoginResponse,
  ReferralCodeCreateRequest,
} from './affiliateTypes';

/** Bearer to use instead of the member session, when one is available. */
export interface AffiliateAuth {
  bearerToken?: string;
}

/**
 * The calls that establish the affiliate identity. A member bearer on them is
 * meaningless, and a 401 must not be mistaken for the member session expiring.
 */
const ANONYMOUS = { anonymous: true } as const;

/**
 * Omitting `auth` entirely is the operator path: the control panel runs these
 * on its member (admin) bearer. But an `auth` that arrived *without* a token is
 * a portal call with no affiliate session, and falling back to the member
 * bearer there would authorise the wrong identity — send nothing instead and
 * let the API reject it.
 */
const withAuth = (auth?: AffiliateAuth) => {
  if (!auth) return undefined;
  return auth.bearerToken ? { bearerToken: auth.bearerToken } : ANONYMOUS;
};

const affiliateApi = {
  list: (
    payload: AffiliateGetAllRequest,
  ): Promise<AxiosResponse<AffiliateGetAllResponse>> =>
    apiClient.post(AFFILIATE_ENDPOINTS.LIST, payload),

  review: (
    payload: AffiliateAdminReviewRequest,
  ): Promise<AxiosResponse<AffiliateMutationResponse>> =>
    apiClient.post(AFFILIATE_ENDPOINTS.ADMIN_REVIEW, payload),

  login: (
    payload: AffiliateValidateLoginRequest,
  ): Promise<AxiosResponse<AffiliateValidateLoginResponse>> =>
    apiClient.post(AFFILIATE_ENDPOINTS.VALIDATE_LOGIN, payload, ANONYMOUS),

  register: (
    payload: AffiliateRegisterRequest,
  ): Promise<AxiosResponse<AffiliateRegisterResponse>> =>
    apiClient.post(AFFILIATE_ENDPOINTS.REGISTER, payload, ANONYMOUS),

  getById: (
    payload: AffiliateGetByIdRequest,
    auth?: AffiliateAuth,
  ): Promise<AxiosResponse<AffiliateGetByIdResponse>> =>
    apiClient.post(AFFILIATE_ENDPOINTS.BY_ID, payload, withAuth(auth)),

  updateProfile: (
    payload: AffiliateUpdateProfileRequest,
    auth?: AffiliateAuth,
  ): Promise<AxiosResponse<AffiliateUpdateProfileResponse>> =>
    apiClient.put(AFFILIATE_ENDPOINTS.UPDATE_PROFILE, payload, withAuth(auth)),

  referralCodes: (
    payload: AffiliateReferralCodeGetListRequest,
    auth?: AffiliateAuth,
  ): Promise<AxiosResponse<AffiliateReferralCodeGetListResponse>> =>
    apiClient.post(AFFILIATE_ENDPOINTS.REFERRAL_CODES, payload, withAuth(auth)),

  createReferralCode: (
    payload: ReferralCodeCreateRequest,
    auth?: AffiliateAuth,
  ): Promise<AxiosResponse<AffiliateMutationResponse>> =>
    apiClient.post(
      AFFILIATE_ENDPOINTS.CREATE_REFERRAL_CODE,
      payload,
      withAuth(auth),
    ),

  updateReferralCode: (
    payload: AffiliateReferralCodeUpdateRequest,
    auth?: AffiliateAuth,
  ): Promise<AxiosResponse<AffiliateMutationResponse>> =>
    apiClient.put(
      AFFILIATE_ENDPOINTS.UPDATE_REFERRAL_CODE,
      payload,
      withAuth(auth),
    ),

  deleteReferralCode: (
    payload: AffiliateReferralCodeDeleteRequest,
    auth?: AffiliateAuth,
  ): Promise<AxiosResponse<AffiliateMutationResponse>> =>
    apiClient.post(
      AFFILIATE_ENDPOINTS.DELETE_REFERRAL_CODE,
      payload,
      withAuth(auth),
    ),
};

export default affiliateApi;
